import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Button } from '@optools/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@optools/components/ui/dropdown-menu';
import { GET_MEDIA_POLICIES } from '@optools/services/operator-gateway/esni/queries';
import { FeedPolicySearchType } from '@optools/services/operator-gateway/esni/types';
import { MediaPolicy } from '@optools/services/operator-gateway/types/generated/graphql';
import { WITH_MiLB_SPORT_IDS } from '@optools/services/statsapi/teams';
import { useTeamsQuery } from '@optools/store/statsApiSlice';
import { EsniDetailsSheet } from '@optools/tools/esni/components/esni-details-sheet';
import { EsniTeamSpot } from '@optools/tools/esni/components/esni-team-spot';
import { SearchBy } from '@optools/tools/esni/components/search-by';
import { useDetailsParams } from '@optools/tools/esni/hooks/useDetailsParams';
import { querySchema } from '@optools/tools/esni/schemas/media';
import { createFileRoute, Link, stripSearchParams, useSearch } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/esni/media')({
  component: RouteComponent,
  validateSearch: zodValidator(querySchema),
  search: {
    middlewares: [stripSearchParams({ searchValue: '' })],
  },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const [open, setOpen] = useState<boolean>(false);
  const { searchType, searchValue } = useSearch({ from: '/_auth/esni/media' });

  const { audienceHref, clearDetailsParam, mediaId, setDetailsParam, viewingPolicyName } =
    useDetailsParams();

  const [selectedMediaPolicy, setSelectedMediaPolicy] = useState<MediaPolicy | undefined>();

  const date = searchType === FeedPolicySearchType.DATE ? searchValue : undefined;
  const gamePk = searchType === FeedPolicySearchType.GAME ? searchValue : undefined;
  const id = searchType === FeedPolicySearchType.MEDIA ? searchValue : undefined;

  const { data, loading } = useQuery(GET_MEDIA_POLICIES, {
    skip: !date && !gamePk && !id,
    variables: { date, gamePk, mediaId: id },
  });

  // Include MiLB Teams
  const { data: teams } = useTeamsQuery(WITH_MiLB_SPORT_IDS);

  const onSheetClose = () => {
    clearDetailsParam();
    setOpen(false);
  };

  const onSelectFeedPolicy = useCallback(
    (mediaPolicy: MediaPolicy) => {
      setSelectedMediaPolicy(mediaPolicy);
      setDetailsParam('media', mediaPolicy.id);
      setOpen(true);
    },
    [setDetailsParam],
  );

  useEffect(() => {
    if (mediaId) {
      const mediaPolicy = data?.mediaPolicies.find((policy) => policy.id === mediaId);
      if (mediaPolicy) {
        setSelectedMediaPolicy(mediaPolicy);
        setOpen(true);
      }
    }
  }, [data?.mediaPolicies, mediaId]);

  const columns = useMemo<ColumnDef<MediaPolicy>[]>(() => {
    return [
      {
        accessorFn: (row) => {
          const team = teams?.find((team) => team.id.toString() === row.feedSubType);
          return team ? `${team.name} ${team.abbreviation} ${row.feedSubType}` : row.feedSubType;
        },
        cell: ({ cell }) => <EsniTeamSpot feedSubType={cell.row.original.feedSubType} />,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Team" />,
        id: 'Team',
      },
      {
        accessorKey: 'id',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Media ID" />,
        id: 'Media ID',
      },
      {
        accessorKey: 'gamePk',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Game PK" />,
        id: 'Game PK',
      },
      {
        accessorKey: 'feedType',
        cell: ({ cell }) => {
          return <span className="capitalize">{cell.row.original.feedType}</span>;
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Feed Type" />,
        id: 'Feed Type',
      },
      {
        accessorKey: 'mediaType',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Media Type" />,
        id: 'Media Type',
      },
      {
        cell: ({ cell }) => {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon-sm" variant="ghost">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onSelectFeedPolicy(cell.row.original)}>
                  View
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    from="/"
                    search={{ mediaId: cell.row.original.id }}
                    to="/esni/rules/process">
                    Process Rules
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        id: 'actions',
        meta: {
          cellClassName: 'flex justify-center',
        },
      },
    ];
  }, [onSelectFeedPolicy, teams]);

  const viewingPolicy = useMemo(
    () => selectedMediaPolicy?.viewingPolicies?.find((policy) => policy.name === viewingPolicyName),
    [selectedMediaPolicy?.viewingPolicies, viewingPolicyName],
  );

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <DataTable columns={columns} data={data?.mediaPolicies ?? []}>
        <SearchBy />
      </DataTable>
      <EsniDetailsSheet
        audienceHref={audienceHref}
        mediaPolicy={selectedMediaPolicy}
        open={open}
        viewingPolicy={viewingPolicy}
        onOpenChange={onSheetClose}
      />
    </ScrollPage>
  );
}
